import "../styles/processForm.css";
import ModuleRecordField from '../components/ModuleRecordField';
import IntegratedAcademicSelect, { isIntegratedAcademicField } from '../components/IntegratedAcademicSelect';
import React, { useEffect, useMemo, useState } from 'react';
import DecimalInput from "../components/DecimalInput";
import IntegerInput from "../components/IntegerInput";
import MaskedInput from "../components/MaskedInput";
import DynamicSelectionField from "../components/DynamicSelectionField";
import TableField from '../components/TableField';
import { useAuth } from '../AuthContext';
import { FaFilePdf } from 'react-icons/fa';
import { evaluateVisibilityExpression } from '../utils/safeExpression';


const getAutoFillValue = (source, user) => {
  if (!source) return '';
  const today = new Date().toISOString().slice(0, 10);
  const sourceMap = {
    user_name: user?.name || '',
    today_date: today,
    user_email: user?.email || '',
    user_cpf: user?.cpf || '',
    user_birth_date: user?.birthDate ? String(user.birthDate).slice(0, 10) : '',
    user_sector: user?.sector?.name || '',
  };
  return sourceMap[source] ?? '';
};

const applyAutoFillDefaults = (fields = [], values = {}, user) => {
  const nextValues = { ...values };
  fields.forEach((field) => {
    if (!field?.id || !field.autoFillSource) return;
    const currentValue = nextValues[field.id];
    const isEmpty = currentValue === undefined || currentValue === null || currentValue === '';
    if (!isEmpty) return;
    const prefilled = getAutoFillValue(field.autoFillSource, user);
    if (prefilled !== '') {
      nextValues[field.id] = prefilled;
    }
  });
  return nextValues;
};

function fieldVisible(field, values) {
  return evaluateVisibilityExpression(field?.visibleWhen, values);
}

export default function FormPreview({ name, groups, fields }) {
  const { user } = useAuth();
  const [values, setValues] = useState({});

  const handleChange = (e) => {
    const { name, value, options, multiple } = e.target;
    const nextValue = multiple
      ? Array.from(options)
          .filter((option) => option.selected)
          .map((option) => option.value)
      : value;

    setValues((v) => ({ ...v, [name]: nextValue }));
  };

  useEffect(() => {
    setValues((prev) => applyAutoFillDefaults(fields, prev, user));
  }, [fields, user]);

  const inputClasses = useMemo(
    () =>
      "w-full rounded-md border border-slate-300 bg-white px-3 py-2 text-sm text-slate-700 shadow-sm transition focus:border-indigo-500 focus:outline-none focus:ring-2 focus:ring-indigo-500/50",
    [],
  );

  const renderFieldControl = (field) => {
    if (isIntegratedAcademicField(field)) {
      return <IntegratedAcademicSelect field={field} fields={fields} values={values} disabled={false} className={inputClasses} onChange={patch => setValues(prev => ({ ...prev, ...patch }))} />;
    }
    if (field.type === 'list') {
      const selectValue = field.allowMultiple
        ? Array.isArray(values[field.id])
          ? values[field.id]
          : values[field.id]
            ? [values[field.id]]
            : []
        : values[field.id] || '';

      return (
        <select
          className={`${inputClasses} appearance-none`}
          id={field.id}
          name={field.id}
          value={selectValue}
          onChange={handleChange}
          required={field.required}
          multiple={field.allowMultiple}
        >
          {!field.allowMultiple && (
            <option value="" disabled>Selecione...</option>
          )}
          {field.options && field.options.map((o, i) => (
            <option key={i} value={o}>{o}</option>
          ))}
        </select>
      );
    }

    if (field.type === 'table') {
      return (
        <TableField
          field={field}
          value={values[field.id]}
          onChange={(nextRows) => setValues((prev) => ({ ...prev, [field.id]: nextRows }))}
          inputClassName={inputClasses}
        />
      );
    }

    if (field.type === 'dynamic-select') {
      return (
        <DynamicSelectionField
          value={values[field.id]}
          onChange={(nextValue) => setValues((prev) => ({ ...prev, [field.id]: nextValue }))}
          readOnly={false}
          allowMultipleDefault={Boolean(field.allowMultiple)}
          required={field.required}
        />
      );
    }
    if (field.type === 'linkedField' && field.linkedModuleKey) {
      return <ModuleRecordField field={field} fields={fields} values={values} className={inputClasses} onChange={patch => setValues(previous => ({ ...previous, ...patch }))} />;
    }
    if (field.type === 'linkedField') {
      return (
        <select
          className={`${inputClasses} appearance-none`}
          id={field.id}
          name={field.id}
          value={values[field.id] || ''}
          onChange={handleChange}
          required={field.required}
        >
          <option value="" disabled>Selecione...</option>
          <option value="preview">As opções serão carregadas do processo de origem</option>
        </select>
      );
    }

    if (field.type === 'signature-attachment') return <div className="signature-field"><strong>Anexo para Assinatura Eletrônica</strong><p>Disponível na execução de uma solicitação. Formato: PDF. Confirmação por código enviado ao e-mail.</p></div>;
    if (field.type === 'document') {
      return (
        <button
          type="button"
          className="inline-flex items-center gap-2 rounded-md border border-indigo-200 bg-indigo-50 px-3 py-2 text-sm font-semibold text-indigo-700 shadow-sm"
        >
          <FaFilePdf /> Gerar PDF: {field.documentTemplate?.title || field.label}
        </button>
      );
    }

    if (field.type === 'file') {
      return (
        <input
          type="file"
          id={field.id}
          className="w-full cursor-pointer rounded-md border border-dashed border-slate-300 bg-white px-3 py-2 text-sm text-slate-700 shadow-sm transition focus:border-indigo-500 focus:outline-none focus:ring-2 focus:ring-indigo-500/50 file:mr-4 file:cursor-pointer file:rounded-md file:border-0 file:bg-indigo-50 file:px-4 file:py-2 file:text-sm file:font-medium file:text-indigo-600 hover:file:bg-indigo-100"
          required={field.required}
        />
      );
    }

    if (field.type === 'decimal') {
      return (
        <DecimalInput
          decimalPlaces={field.decimalPlaces || 0}
          name={field.id}
          id={field.id}
          value={values[field.id] || ''}
          onChange={handleChange}
          className={inputClasses}
          required={field.required}
        />
      );
    }

    if (field.type === 'number') {
      return (
        <IntegerInput
          name={field.id}
          id={field.id}
          value={values[field.id] || ''}
          onChange={handleChange}
          className={inputClasses}
          required={field.required}
        />
      );
    }

    if (field.type === 'phone') {
      return (
        <MaskedInput
          mask="phone"
          name={field.id}
          id={field.id}
          value={values[field.id] || ''}
          onChange={handleChange}
          className={inputClasses}
          required={field.required}
        />
      );
    }

    if (field.type === 'currency') {
      return (
        <MaskedInput
          mask="currency"
          name={field.id}
          id={field.id}
          value={values[field.id] || ''}
          onChange={handleChange}
          className={inputClasses}
          required={field.required}
        />
      );
    }

    if (field.type === 'cpf' || field.mask === 'cpf') {
      return (
        <MaskedInput
          mask="cpf"
          name={field.id}
          id={field.id}
          value={values[field.id] || ''}
          onChange={handleChange}
          className={inputClasses}
          required={field.required}
        />
      );
    }

    if (field.type === 'cnpj') {
      return (
        <MaskedInput
          mask="cnpj"
          name={field.id}
          id={field.id}
          value={values[field.id] || ''}
          onChange={handleChange}
          className={inputClasses}
          required={field.required}
        />
      );
    }

    if (field.type === 'cep') {
      return (
        <MaskedInput
          mask="cep"
          name={field.id}
          id={field.id}
          value={values[field.id] || ''}
          onChange={handleChange}
          className={inputClasses}
          required={field.required}
        />
      );
    }

    if (field.type === 'email') {
      return (
        <input
          type="email"
          name={field.id}
          id={field.id}
          value={values[field.id] || ''}
          onChange={handleChange}
          className={inputClasses}
          required={field.required}
          readOnly={Boolean(field.autoFillSource) && field.autoFillEditable === false}
          inputMode="email"
          autoComplete="email"
          pattern="[^@\s]+@[^@\s]+\.[^@\s]+"
        />
      );
    }

    if (field.type === 'date') {
      return (
        <input
          type="date"
          name={field.id}
          id={field.id}
          value={values[field.id] || ''}
          onChange={handleChange}
          className={inputClasses}
          required={field.required}
          readOnly={Boolean(field.autoFillSource) && field.autoFillEditable === false}
          onFocus={(e) => e.target.showPicker?.()}
        />
      );
    }

    if (field.type === 'time') {
      return (
        <input
          type="time"
          name={field.id}
          id={field.id}
          value={values[field.id] || ''}
          onChange={handleChange}
          className={inputClasses}
          required={field.required}
          readOnly={Boolean(field.autoFillSource) && field.autoFillEditable === false}
        />
      );
    }

    if (field.type === 'textarea') {
      const textareaRows = Math.max(3, Number(field.rows) || 0);
      return (
        <textarea
          name={field.id}
          id={field.id}
          value={values[field.id] || ''}
          onChange={handleChange}
          rows={textareaRows}
          className={`${inputClasses} resize-y`}
          required={field.required}
          readOnly={Boolean(field.autoFillSource) && field.autoFillEditable === false}
        />
      );
    }

    return (
      <input
        type={field.type}
        name={field.id}
        id={field.id}
        value={values[field.id] || ''}
        onChange={handleChange}
        className={inputClasses}
        required={field.required}
        readOnly={Boolean(field.autoFillSource) && field.autoFillEditable === false}
      />
    );
  };

  return (
    <div className="process-form space-y-4 p-4 sm:p-6">
      <h1 className="text-2xl">Prévia: {name}</h1>
      {groups.map((g) => {
        const gFields = fields.filter((f) => f.groupId === g.id && fieldVisible(f, values));
        if (gFields.length === 0) return null;
        return (
          <div key={g.id} className="process-form-group mb-4">
            <h3 className="mb-1 text-lg font-medium">{g.label}</h3>
            <div className="grid grid-cols-12 gap-4">
              {gFields.map((f, idx) => (
                <div
                  key={`${g.id}-${idx}`}
                  className="flex flex-col gap-2"
                  style={{ gridColumn: `span ${f.size} / span ${f.size}` }}
                >
                  <div className="flex flex-col gap-1">
                    <label className="text-sm font-medium text-slate-700" htmlFor={f.id}>
                      {f.label}
                      {f.required && <span className="text-red-600"> *</span>}
                    </label>
                    {f.description && (
                      <span className="text-xs leading-snug text-slate-500">{f.description}</span>
                    )}
                  </div>
                  {renderFieldControl(f)}
                </div>
              ))}
            </div>
          </div>
        );
      })}
      {fields.filter((f) => !f.groupId && fieldVisible(f, values)).length > 0 && (
        <div className="process-form-group mb-4">
          {groups.length > 0 && (
            <h3 className="mb-1 text-lg font-medium">Campos</h3>
          )}
          <div className="grid grid-cols-12 gap-4">
            {fields
              .filter((f) => !f.groupId && fieldVisible(f, values))
              .map((f, idx) => (
                <div
                  key={`nogroup-prev-${idx}`}
                  className="flex flex-col gap-2"
                  style={{ gridColumn: `span ${f.size} / span ${f.size}` }}
                >
                  <div className="flex flex-col gap-1">
                    <label className="text-sm font-medium text-slate-700" htmlFor={f.id}>
                      {f.label}
                      {f.required && <span className="text-red-600"> *</span>}
                    </label>
                    {f.description && (
                      <span className="text-xs leading-snug text-slate-500">{f.description}</span>
                    )}
                  </div>
                  {renderFieldControl(f)}
                </div>
              ))}
          </div>
        </div>
      )}
    </div>
  );
}
